import { useEffect, useState } from "react";

import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

import copy from "copy-to-clipboard";

import QRCode from "@alpacahq/qrcode.react";
import { Button, Modal, toastError, toastSuccess } from "@alpacahq/ui/old";

import {
  createRecoveryCode as apiCreateRecovery,
  deleteRecoveryCode as apiDeleteRecovery,
} from "~/v2/api/rest/recovery";
import { OTPInput } from "~/v2/components/common";

type MFAType = "SOFTWARE_TOKEN_MFA" | "SMS_MFA";

// -- ProfileMFA ---------------------------------------------------------------

interface ProfileMFAProps {
  type: string;
  title: string;
  securityLevel: string;
  onClick: () => void;
  disabled?: boolean;
  current: string | null;
}

export const ProfileMFA = ({
  type,
  title,
  securityLevel,
  onClick,
  disabled,
  current,
}: ProfileMFAProps) => {
  const isActive = !disabled;
  const securityColor = type === "sms" ? "text-yellow-600" : "text-green-600";

  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex flex-col">
        <span className="font-medium text-neutral-900 text-sm">{title}</span>
        <span className={`text-xs ${securityColor}`}>{securityLevel}</span>
      </div>
      <Button
        variant="v2/stroke"
        disabled={current !== "NOMFA" && disabled}
        onClick={onClick}
      >
        {isActive ? "Disable" : "Activate"}
      </Button>
    </div>
  );
};

// -- TOTPScreen ---------------------------------------------------------------

const getTOTPPlainTextCode = (totpQRCode: string): string => {
  try {
    return totpQRCode.split("secret=")[1].split("&issuer=")[0];
  } catch {
    return "No code available, please try again.";
  }
};

interface TOTPScreenProps {
  totpQRCode: string;
  onCancel: () => void;
  onNext: () => void;
}

const TOTPScreen = ({ totpQRCode, onCancel, onNext }: TOTPScreenProps) => {
  const [isText, setIsText] = useState(false);
  const textCode = getTOTPPlainTextCode(totpQRCode);

  return (
    <div className="flex flex-col items-center">
      <p className="mb-6 text-neutral-500 text-sm">
        {isText
          ? "Copy this code into your authenticator app."
          : "Scan this QR code with your authenticator app."}
      </p>

      <div className="mb-6">
        {isText ? (
          <button
            type="button"
            className="break-all rounded border border-neutral-200 bg-neutral-50 px-4 py-3 text-left font-mono text-sm"
            onClick={() => {
              copy(textCode);
              toastSuccess("Code copied to clipboard!");
            }}
          >
            {textCode}
          </button>
        ) : (
          <QRCode value={totpQRCode || ""} size={180} />
        )}
      </div>

      <button
        type="button"
        className="mb-8 text-neutral-500 text-sm underline"
        onClick={() => setIsText((v) => !v)}
      >
        {isText ? "Show QR code" : "Enter code manually"}
      </button>

      <div className="flex w-full gap-3">
        <Button variant="v2/stroke" className="flex-1" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="v2/filled" className="flex-1" onClick={onNext}>
          Next
        </Button>
      </div>
    </div>
  );
};

// -- SMSScreen ----------------------------------------------------------------

interface SMSScreenProps {
  onNext: () => void;
  onCancel: () => void;
  onSubmit: (phoneNumber: string) => void;
}

const SMSScreen = ({ onNext, onCancel, onSubmit }: SMSScreenProps) => {
  const [phoneNumber, setPhoneNumber] = useState("");

  return (
    <div className="flex flex-col items-center">
      <p className="mb-6 text-neutral-500 text-sm">
        Enter your phone number to receive verification codes via text message.
      </p>

      <div className="mb-8 w-full max-w-[280px]">
        <PhoneInput
          inputClass="block w-full h-9 rounded border bg-white text-sm font-medium transition-colors focus:ring-0 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-300 disabled:placeholder:text-gray-300 focus:border-gray-500 border-gray-400 placeholder-shown:border-gray-400 text-gray-700 placeholder-gray-400"
          containerClass="w-full"
          onChange={(phone: string) => setPhoneNumber(`+${phone}`)}
          country="us"
          placeholder="+1 XXX-XXX-XXXX"
        />
      </div>

      <div className="flex w-full gap-3">
        <Button variant="v2/stroke" className="flex-1" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="v2/filled"
          className="flex-1"
          onClick={() => {
            onNext();
            onSubmit(phoneNumber);
          }}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

// -- MFAChallenge -------------------------------------------------------------

interface MFAChallengeProps {
  activating: boolean;
  type: MFAType;
  onVerify: (code: string) => void | Promise<void>;
  onBack?: () => void;
  onCancel: () => void;
  error?: string;
}

const MFAChallenge = ({
  activating,
  type,
  onVerify,
  onBack,
  onCancel,
  error,
}: MFAChallengeProps) => {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const source = type === "SOFTWARE_TOKEN_MFA" ? "authenticator app" : "SMS";

  const handleVerify = async () => {
    setLoading(true);
    try {
      await onVerify(code);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Verification failed. Please try again.";
      toastError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center">
      <p className="mb-6 text-neutral-500 text-sm">
        Enter the 6-digit code from your {source} to confirm.
      </p>

      <OTPInput onChange={setCode} disabled={loading} />

      {error && <p className="mt-3 text-red-600 text-sm">{error}</p>}

      <div className="mt-8 flex w-full gap-3">
        <Button
          variant="v2/stroke"
          className="flex-1"
          onClick={onBack ?? onCancel}
          disabled={loading}
        >
          {onBack ? "Back" : "Cancel"}
        </Button>
        <Button
          variant={activating ? "v2/filled" : "red"}
          className="flex-1"
          onClick={handleVerify}
          disabled={code.length !== 6 || loading}
          loading={loading}
        >
          {activating ? "Activate" : "Disable"}
        </Button>
      </div>
    </div>
  );
};

// -- CompletedView ------------------------------------------------------------

interface CompletedViewProps {
  activating: boolean;
  cognitoId: string;
}

const CompletedView = ({ activating, cognitoId }: CompletedViewProps) => {
  const [recoveryCode, setRecoveryCode] = useState("");

  // biome-ignore lint/correctness/useExhaustiveDependencies: deliberately mount-only: this creates or deletes the Cognito recovery codes once. Re-running on activating/cognitoId churn would re-issue codes and invalidate the ones just shown to the user.
  useEffect(() => {
    if (activating) {
      apiCreateRecovery(cognitoId)
        .then((res) => setRecoveryCode(res.code))
        .catch(() =>
          toastError("An error occurred while creating recovery codes."),
        );
    } else {
      apiDeleteRecovery(cognitoId).catch((e) => console.error(e));
    }
  }, []);

  return (
    <div className="flex flex-col items-center text-center">
      <p className="text-neutral-500 text-sm">
        {activating
          ? "Your account is now more secure."
          : "You can re-enable two-factor authentication anytime."}
      </p>

      {activating && recoveryCode && (
        <div className="mt-6 w-full rounded-lg border border-neutral-200 bg-neutral-50 p-4 text-left">
          <p className="mb-2 font-medium text-neutral-900 text-sm">
            Recovery code
          </p>
          <p className="mb-3 text-neutral-500 text-xs">
            Save this code somewhere safe. You can use it to sign in if you lose
            access to your authenticator app.
          </p>
          <div className="flex items-center justify-between rounded border border-neutral-200 bg-white px-3 py-2">
            <span className="font-mono text-sm">{recoveryCode}</span>
            <button
              type="button"
              className="text-neutral-400 text-xs underline hover:text-neutral-600"
              onClick={() => {
                copy(recoveryCode);
                toastSuccess("Recovery code copied!");
              }}
            >
              Copy
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// -- MFAModal -----------------------------------------------------------------

const methodLabel: Record<string, string> = {
  SOFTWARE_TOKEN_MFA: "authenticator app",
  SMS_MFA: "SMS",
};

interface MFAModalProps {
  isOpen: boolean;
  onClose: () => void;
  activating: boolean;
  type: MFAType;
  currentMethod: string | null;
  onVerify: (code: string) => void | Promise<void>;
  cognitoId?: string;
  totpQRCode?: string;
  onSubmitPhone?: (phoneNumber: string) => void;
  error?: string;
}

export const MFAModal = ({
  isOpen,
  onClose,
  activating,
  type,
  currentMethod,
  onVerify,
  cognitoId = "",
  totpQRCode = "",
  onSubmitPhone,
  error,
}: MFAModalProps) => {
  const [step, setStep] = useState(activating ? 0 : 1);

  const completed =
    (!activating && currentMethod === "NOMFA") ||
    (activating && currentMethod !== "NOMFA");

  const getTitle = () => {
    if (completed) {
      return activating
        ? "Two-factor authentication enabled"
        : "Two-factor authentication disabled";
    }
    if (step === 0) {
      return `Set up ${methodLabel[type]}`;
    }
    return activating
      ? "Verify your code"
      : "Disable two-factor authentication";
  };

  const renderContent = () => {
    if (completed) {
      return <CompletedView activating={activating} cognitoId={cognitoId} />;
    }

    if (step === 0) {
      if (type === "SMS_MFA" && onSubmitPhone) {
        return (
          <SMSScreen
            onNext={() => setStep(1)}
            onCancel={onClose}
            onSubmit={onSubmitPhone}
          />
        );
      }
      return (
        <TOTPScreen
          totpQRCode={totpQRCode}
          onCancel={onClose}
          onNext={() => setStep(1)}
        />
      );
    }

    return (
      <MFAChallenge
        activating={activating}
        type={type}
        onVerify={onVerify}
        onBack={activating ? () => setStep(0) : undefined}
        onCancel={onClose}
        error={error}
      />
    );
  };

  return (
    <Modal title={getTitle()} isOpen={isOpen} onClose={onClose}>
      {renderContent()}
    </Modal>
  );
};
